import { startCase } from 'lodash-es'
import { useFormContext, useWatch } from 'react-hook-form'
import { AddressState } from 'schemaTypes'
import { requiredFn } from 'utility/validators'
import { EhrCoachGender } from 'types'
import { Field } from '../Field'
import {
  InputStyled,
  RowStyled,
  SectionStyled,
  SelectStyled,
  SelectWrapStyled
} from '../onboardingStyles'
import { ICoachProfileFormValues } from '../types'

const STATE_OPTIONS = Object.values(AddressState)
// Explicit order (Female, Male first) rather than the enum's alphabetical order.
const GENDER_OPTIONS = [
  EhrCoachGender.Female,
  EhrCoachGender.Male,
  EhrCoachGender.NonBinary,
  EhrCoachGender.Genderqueer,
  EhrCoachGender.PreferToSelfDescribe,
  EhrCoachGender.PreferNotToSay
]

export const PersonalSection = () => {
  const {
    register,
    control,
    formState: { errors }
  } = useFormContext<ICoachProfileFormValues>()
  // Watched so the `is-placeholder` styling on the native selects tracks RHF state without
  // forcing them through `Controller` (which had a controlled-component first-change bug).
  const gender = useWatch({ control, name: 'gender' })
  const stateOfResidence = useWatch({ control, name: 'stateOfResidence' })

  return (
    <SectionStyled id="section-personal">
      <div className="section-head">
        <div className="heading-block">
          <span className="num" aria-hidden="true">
            01
          </span>
          <div>
            <h2>Personal</h2>
            <div className="blurb">Basic details so clients can match with you.</div>
          </div>
        </div>
      </div>

      <RowStyled>
        <Field htmlFor="firstName" label="First name" required error={errors.firstName?.message}>
          <InputStyled
            id="firstName"
            aria-invalid={!!errors.firstName || undefined}
            {...register('firstName', { validate: requiredFn })}
          />
        </Field>
        <Field htmlFor="lastName" label="Last name" required error={errors.lastName?.message}>
          <InputStyled
            id="lastName"
            aria-invalid={!!errors.lastName || undefined}
            {...register('lastName', { validate: requiredFn })}
          />
        </Field>
      </RowStyled>

      <RowStyled>
        <Field htmlFor="gender" label="Gender" required error={errors.gender?.message}>
          <SelectWrapStyled>
            <SelectStyled
              id="gender"
              className={gender ? undefined : 'is-placeholder'}
              aria-invalid={!!errors.gender || undefined}
              {...register('gender', {
                validate: (value) => (value ? undefined : 'Please select an option.')
              })}
            >
              <option value="">Select…</option>
              {GENDER_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {startCase(option)}
                </option>
              ))}
            </SelectStyled>
          </SelectWrapStyled>
        </Field>
        <Field
          htmlFor="stateOfResidence"
          label="State of residence"
          required
          error={errors.stateOfResidence?.message}
        >
          <SelectWrapStyled>
            <SelectStyled
              id="stateOfResidence"
              className={stateOfResidence ? undefined : 'is-placeholder'}
              aria-invalid={!!errors.stateOfResidence || undefined}
              {...register('stateOfResidence', { validate: requiredFn })}
            >
              <option value="">Select your state…</option>
              {STATE_OPTIONS.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </SelectStyled>
          </SelectWrapStyled>
        </Field>
      </RowStyled>

      <Field
        htmlFor="ethnicity"
        label="Ethnicity"
        optional
        help="Some clients prefer a coach who shares their background. Never shown without your permission."
      >
        <InputStyled
          id="ethnicity"
          placeholder="e.g., Mexican-American"
          {...register('ethnicity')}
        />
      </Field>

      <Field
        htmlFor="additionalLanguages"
        label="Additional languages"
        optional
        help="Beyond English. Comma-separated."
        error={errors.additionalLanguages?.message}
      >
        <InputStyled
          id="additionalLanguages"
          placeholder="e.g., Spanish, Portuguese"
          aria-invalid={!!errors.additionalLanguages || undefined}
          {...register('additionalLanguages')}
        />
      </Field>
    </SectionStyled>
  )
}
